import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useMediaQuery, useTheme, TextField, InputAdornment, Chip, Button,
    FormControl, InputLabel, Select, MenuItem, Collapse, Tooltip, CircularProgress,
    TablePagination, Menu, ListItemIcon, ListItemText, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PriceChangeIcon from '@mui/icons-material/PriceChange';

import api from '../../services/api';
import { isVisualizador } from '../../services/auth';
import { toastSuccess, confirmAcao } from '../../services/alerts';
import { formatarCnpj, formatarTelefone } from '../../services/convenios';

export default function Convenios() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const soLeitura = isVisualizador();

    const [convenios, setConvenios] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [busca, setBusca] = useState('');
    const [debouncedBusca, setDebouncedBusca] = useState('');
    const [ativoFilter, setAtivoFilter] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const [menuAnchor, setMenuAnchor] = useState({ el: null, convenio: null });
    const abrirMenu = (e, convenio) => setMenuAnchor({ el: e.currentTarget, convenio });
    const fecharMenu = () => setMenuAnchor(prev => ({ ...prev, el: null }));

    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 300);
        return () => clearTimeout(t);
    }, [busca]);

    const hasActiveFilters = ativoFilter !== '';

    const carregarDados = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, size: rowsPerPage };
            if (debouncedBusca) params.busca = debouncedBusca;
            if (ativoFilter !== '') params.ativo = ativoFilter;
            const res = await api.get('/planos-saude/paginado', { params });
            setConvenios(res.data?.content ?? []);
            setTotalElements(res.data?.totalElements ?? 0);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, debouncedBusca, ativoFilter]);

    useEffect(() => { carregarDados(); }, [carregarDados]);
    useEffect(() => { setPage(0); }, [debouncedBusca, ativoFilter]);

    const handleAlterarStatus = async (item) => {
        fecharMenu();
        const inativando = item.ativo;
        const ok = await confirmAcao({
            title: inativando ? 'Inativar convênio' : 'Reativar convênio',
            text: inativando
                ? 'O convênio deixa de aceitar novas carteirinhas, guias e lotes, mas todo o histórico é mantido. Continuar?'
                : 'Reativar este convênio?',
            confirmButtonText: inativando ? 'Inativar' : 'Reativar',
            icon: inativando ? 'warning' : 'question',
            danger: inativando,
        });
        if (!ok) return;
        try {
            await api.patch(`/planos-saude/${item.id_plano_saude}/${inativando ? 'inativar' : 'reativar'}`);
            toastSuccess(inativando ? 'Convênio inativado' : 'Convênio reativado');
            carregarDados();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    const handleExcluir = async (item) => {
        fecharMenu();
        const ok = await confirmAcao({
            title: 'Excluir convênio',
            text: `Confirma exclusão de "${item.nome}"? Só é possível enquanto não houver carteirinha nem tabela de valores. Para convênio já usado, inative.`,
            confirmButtonText: 'Excluir',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/planos-saude/${item.id_plano_saude}`);
            toastSuccess('Convênio excluído');
            carregarDados();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="div" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Convênios
                </Typography>
                {!soLeitura && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/convenios/novo')}>
                        Novo Convênio
                    </Button>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: isMobile ? 1.5 : 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Buscar por nome, registro ANS ou CNPJ..."
                        variant="outlined"
                        fullWidth
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                            endAdornment: busca && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setBusca('')} edge="end"><ClearIcon /></IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Filtros avançados">
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: showFilters || hasActiveFilters ? 'primary.main' : '#e0e0e0',
                                    borderRadius: 2, p: 1.2,
                                    bgcolor: showFilters || hasActiveFilters ? 'rgba(25,118,210,0.08)' : 'transparent',
                                    color: showFilters || hasActiveFilters ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                {showFilters ? <FilterListOffIcon /> : <FilterListIcon />}
                            </IconButton>
                            {hasActiveFilters && !showFilters && (
                                <Box sx={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, bgcolor: 'error.main', borderRadius: '50%' }} />
                            )}
                        </Box>
                    </Tooltip>
                </Box>

                <Collapse in={showFilters}>
                    <Box sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Status</InputLabel>
                            <Select value={ativoFilter} label="Status" onChange={(e) => setAtivoFilter(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value={true}>Ativos</MenuItem>
                                <MenuItem value={false}>Inativos</MenuItem>
                            </Select>
                        </FormControl>
                        {hasActiveFilters && (
                            <Button variant="text" color="error" size="small" onClick={() => setAtivoFilter('')} sx={{ fontWeight: 'bold' }}>
                                Limpar filtros
                            </Button>
                        )}
                    </Box>
                </Collapse>

                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
                    {totalElements} registro(s){(debouncedBusca || hasActiveFilters) && ' • Filtros aplicados'}
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: '#1ABB9C' }} />
                    </Box>
                ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size={isMobile ? 'small' : 'medium'}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell><strong>Convênio</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Registro ANS</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>CNPJ</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Contato</strong></TableCell>
                                    <TableCell align="center"><strong>Procedimentos</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Repasse</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell align="right"><strong>Ações</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {convenios.length > 0 ? convenios.map((item) => (
                                    <TableRow key={item.id_plano_saude} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LocalHospitalIcon fontSize="small" color="action" />
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.nome}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>{item.registroAns || '-'}</TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>{item.cnpjFormatado || formatarCnpj(item.cnpj) || '-'}</TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            {item.telefone ? formatarTelefone(item.telefone) : (item.email || '-')}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip size="small" label={item.quantidadeProcedimentos ?? 0} variant="outlined" />
                                        </TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            {item.prazoRepasseDias != null ? `${item.prazoRepasseDias} dias` : '-'}
                                            {item.percentualRepasse != null && ` • ${item.percentualRepasse}%`}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={item.ativo ? 'Ativo' : 'Inativo'} color={item.ativo ? 'success' : 'default'} size="small" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={(e) => abrirMenu(e, item)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" sx={{ color: '#999' }}>
                                                Nenhum convênio encontrado.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Menu anchorEl={menuAnchor.el} open={Boolean(menuAnchor.el)} onClose={fecharMenu}>
                    <MenuItem onClick={() => { fecharMenu(); navigate(`/convenios/${menuAnchor.convenio?.id_plano_saude}`); }}>
                        <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText>{soLeitura ? 'Visualizar' : 'Editar'}</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { fecharMenu(); navigate(`/convenios/${menuAnchor.convenio?.id_plano_saude}/procedimentos`); }}>
                        <ListItemIcon><PriceChangeIcon fontSize="small" color="action" /></ListItemIcon>
                        <ListItemText>Tabela de valores</ListItemText>
                    </MenuItem>
                    {!soLeitura && (
                        <MenuItem onClick={() => handleAlterarStatus(menuAnchor.convenio)}>
                            <ListItemIcon>
                                {menuAnchor.convenio?.ativo
                                    ? <BlockIcon fontSize="small" color="warning" />
                                    : <CheckCircleIcon fontSize="small" color="success" />}
                            </ListItemIcon>
                            <ListItemText>{menuAnchor.convenio?.ativo ? 'Inativar' : 'Reativar'}</ListItemText>
                        </MenuItem>
                    )}
                    {!soLeitura && (
                        <MenuItem onClick={() => handleExcluir(menuAnchor.convenio)}>
                            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText>Excluir</ListItemText>
                        </MenuItem>
                    )}
                </Menu>

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Linhas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Paper>
        </Box>
    );
}
